import { useEffect, useState, useCallback } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost, apiFetch } from '../lib/api';
import { Feedback } from '../components/Feedback';
import { CopyButton } from '../components/CopyButton';

interface UserRow {
  id: number;
  email: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'invited' | 'disabled';
  createdAt: number;
  lastLoginAt: number | null;
  disabledAt: number | null;
  schemas: string[];
}

interface ListResponse { users: UserRow[] }

interface PendingInvite {
  id: number;
  email: string;
  role: 'admin' | 'member';
  schemas: string[];
  invitedByUserId: number;
  createdAt: number;
  expiresAt: number;
}

interface InvitationsResponse { invitations: PendingInvite[] }

interface InviteResponse {
  invitationId: number;
  email: string;
  role: 'admin' | 'member';
  schemas: string[];
  inviteToken: string;
  expiresAt: number;
}

interface SchemaListItem { id: number; code: string }

function fmt(t: number | null): string {
  if (!t) return 'never';
  const ms = t > 1e12 ? t : t * 1000;
  return new Date(ms).toLocaleString();
}

/**
 * Route: `/dashboard/members`. Owner/admin only.
 *
 * List, invite, disable, and re-enable users in the org. Members can be
 * scoped to specific schemas; owners and admins are unrestricted.
 *
 * Invitations return a one-time token that the dashboard surfaces inline
 * for the inviter to copy + share manually (no email sender wired up yet).
 */
export function MembersPage() {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [schemas, setSchemas] = useState<SchemaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'warn'; msg: string } | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [revealedInvite, setRevealedInvite] = useState<InviteResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, iRes] = await Promise.all([
        apiGet<ListResponse>('/v1/users'),
        apiGet<InvitationsResponse>('/v1/users/invitations'),
      ]);
      setUsers(uRes.users);
      setPendingInvites(iRes.invitations);
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'failed to load users' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    apiGet<{ schemas: SchemaListItem[] }>('/v1/schemas')
      .then((res) => setSchemas(res.schemas ?? []))
      .catch(() => setSchemas([]));
  }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <div className="text-muted" style={{ marginBottom: 8 }}>── members ──────────────────────────────────────</div>
        <p>only owners and admins can manage org members.</p>
      </div>
    );
  }

  async function disable(id: number, email: string) {
    if (!confirm(`Disable ${email}? Their sessions will be revoked. API keys they created remain active until you rotate them.`)) return;
    try {
      await apiPost(`/v1/users/${id}/disable`, {});
      await load();
      setFeedback({ type: 'success', msg: `${email} disabled.` });
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'disable failed' });
    }
  }

  async function revokeInvite(id: number, email: string) {
    if (!confirm(`Revoke pending invitation for ${email}? The link will stop working immediately.`)) return;
    try {
      await apiPost(`/v1/users/invitations/${id}/revoke`, {});
      await load();
      setFeedback({ type: 'success', msg: `invitation for ${email} revoked.` });
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'revoke failed' });
    }
  }

  async function reenable(id: number, email: string) {
    try {
      await apiPost(`/v1/users/${id}/reenable`, {});
      await load();
      setFeedback({ type: 'success', msg: `${email} re-enabled. They will need to log in again.` });
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'reenable failed' });
    }
  }

  return (
    <div>
      <div className="text-muted" style={{ marginBottom: 8 }}>── members ──────────────────────────────────────</div>
      <p className="text-muted" style={{ marginBottom: 16, fontSize: 'var(--font-size-sm)' }}>
        humans inside <strong>{user?.orgName ?? '—'}</strong>. owners and admins are unrestricted.
        members are scoped to a specific list of standards.
      </p>

      {feedback && <Feedback type={feedback.type} message={feedback.msg} />}

      {revealedInvite && (() => {
        const inviteUrl = `${window.location.origin}/accept-invite?token=${encodeURIComponent(revealedInvite.inviteToken)}`;
        const expiresStr = new Date(revealedInvite.expiresAt * 1000).toLocaleString();
        const orgName = user?.orgName ?? 'an AstdZ organization';
        const inviterName = user?.email ?? 'an admin';
        const emailSubject = `You're invited to join ${orgName} on AstdZ`;
        const emailBody =
`Hi,

${inviterName} has invited you to join ${orgName} on AstdZ as a ${revealedInvite.role}${revealedInvite.schemas.length > 0 ? ` (scoped to ${revealedInvite.schemas.join(', ')})` : ''}.

Click the link below to set your password and activate your account:

${inviteUrl}

This link expires on ${expiresStr} and can only be used once.

AstdZ — when the towers go down, the mesh goes up
https://astdz.com`;
        const mailto = `mailto:${encodeURIComponent(revealedInvite.email)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        return (
          <div className="box" style={{ marginBottom: 16, borderColor: 'var(--color-accent)' }}>
            <p className="label">invitation ready — send to {revealedInvite.email}</p>
            <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 12 }}>
              role: <strong>{revealedInvite.role}</strong>
              {revealedInvite.schemas.length > 0 && (
                <> · schemas: <code>{revealedInvite.schemas.join(', ')}</code></>
              )}
              {' · '}expires {expiresStr}
            </p>

            <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 4 }}>invite link:</p>
            <pre className="hex-display" style={{ overflowWrap: 'break-word', whiteSpace: 'pre-wrap', marginBottom: 8 }}>{inviteUrl}</pre>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <CopyButton text={inviteUrl} label="copy link" />
            </div>

            <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 4 }}>email body (paste into your mail client):</p>
            <pre className="hex-display" style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>{emailBody}</pre>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <CopyButton text={emailBody} label="copy email body" />
              <a href={mailto} className="btn">→ open in mail app</a>
              <button type="button" className="btn" onClick={() => setRevealedInvite(null)}>done</button>
            </div>
            <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 8 }}>
              link is single-use. if it expires before they accept, just create a new invitation.
            </p>
          </div>
        );
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>{users.length} user{users.length === 1 ? '' : 's'}</span>
        <button type="button" className="btn btn-primary" onClick={() => setShowInvite((v) => !v)}>
          {showInvite ? 'cancel' : '+ invite member'}
        </button>
      </div>

      {showInvite && (
        <InviteForm
          schemas={schemas}
          onCancel={() => setShowInvite(false)}
          onInvited={(res) => {
            setShowInvite(false);
            setRevealedInvite(res);
          }}
        />
      )}

      {pendingInvites.length > 0 && (
        <>
          <div className="text-muted" style={{ marginBottom: 8, marginTop: 8 }}>── pending invitations ─────────────────────────</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {pendingInvites.map((inv) => (
              <div key={inv.id} className="box">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong>{inv.email}</strong>
                      <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>· {inv.role}</span>
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-warn)', padding: '1px 6px', border: '1px solid var(--color-warn)', borderRadius: 4 }}>
                        pending
                      </span>
                    </div>
                    {inv.role === 'member' && inv.schemas.length > 0 && (
                      <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                        <span className="text-muted">schemas:</span>{' '}
                        {inv.schemas.map((s) => <code key={s} style={{ marginRight: 6 }}>{s}</code>)}
                      </div>
                    )}
                    <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                      <span className="text-muted">invited:</span> {fmt(inv.createdAt)}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)' }}>
                      <span className="text-muted">expires:</span> {fmt(inv.expiresAt)}
                    </div>
                    <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 6 }}>
                      original invite link cannot be retrieved (token is hashed). to re-send, revoke and create a new invitation.
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button type="button" className="btn" onClick={() => revokeInvite(inv.id, inv.email)}>revoke</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-muted" style={{ marginBottom: 8 }}>── members ──────────────────────────────────────</div>
        </>
      )}

      {loading ? (
        <p className="text-muted">loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {users.map((u) => (
            <div key={u.id} className="box">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong>{u.email}</strong>
                    <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>· {u.role}</span>
                    <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>· {u.status}</span>
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                    <span className="text-muted">last login:</span> {fmt(u.lastLoginAt)}
                  </div>
                  {u.role === 'member' && (
                    <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 2 }}>
                      <span className="text-muted">schemas:</span>{' '}
                      {u.schemas.length === 0 ? <em className="text-muted">none</em> : u.schemas.map((s) => <code key={s} style={{ marginRight: 6 }}>{s}</code>)}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {u.role !== 'owner' && u.id !== user?.id && (
                    u.status === 'disabled' ? (
                      <button type="button" className="btn" onClick={() => reenable(u.id, u.email)}>re-enable</button>
                    ) : (
                      <button type="button" className="btn" onClick={() => disable(u.id, u.email)}>disable</button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface InviteFormProps {
  schemas: SchemaListItem[];
  onCancel: () => void;
  onInvited: (res: InviteResponse) => void;
}

function InviteForm({ schemas, onCancel, onInvited }: InviteFormProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('enter a valid email address');
      return;
    }
    if (role === 'member' && selected.size === 0) {
      setError('members need at least one schema');
      return;
    }
    setBusy(true);
    try {
      const body = {
        email,
        role,
        schemas: role === 'member' ? Array.from(selected) : [],
      };
      const res = await apiFetch<InviteResponse>('/v1/users/invite', { method: 'POST', body });
      onInvited(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'invite failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="box" style={{ marginBottom: 16 }}>
      <p className="label">invite member</p>
      {error && <Feedback type="error" message={error} />}

      <div className="form-row">
        <label className="label">email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="form-input"
          autoComplete="email"
        />
      </div>

      <div className="form-row">
        <label className="label">role</label>
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="radio" name="role" checked={role === 'member'} onChange={() => setRole('member')} />
            <span>member (scoped)</span>
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="radio" name="role" checked={role === 'admin'} onChange={() => setRole('admin')} />
            <span>admin (unrestricted)</span>
          </label>
        </div>
      </div>

      {role === 'member' && (
        <div className="form-row">
          <label className="label">schema allowlist</label>
          {schemas.length === 0 ? (
            <p className="text-muted">no schemas available</p>
          ) : (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {schemas.map((s) => (
                <label key={s.code} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selected.has(s.code)} onChange={() => toggle(s.code)} />
                  <code>{s.code}</code>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'inviting…' : 'create invitation'}
        </button>
        <button type="button" className="btn" onClick={onCancel}>cancel</button>
      </div>
    </form>
  );
}
